import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import { showErrorSnackbar, type ErrorSnackbarContext } from "_shared/_errors/error-snackbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { viewNameProblem } from "_shared/_views/list-pages/current-table-view";
import {
  fromTableViewStateJSON,
  useDeleteTableViewMutation,
  useReorderTableViewsMutation,
  useStarTableViewMutation,
  useUpdateTableViewMutation,
} from "_shared/_views/tables/_api/table-views-api";
import type { NamedTableViewJSON } from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import { useId, useState, type FormEvent } from "react";
import { useSession } from "users/__session/session-context";

/** What the dialog says when a change to the views could not be made. */
export const MANAGE_VIEWS_FAILURE: ErrorSnackbarContext = {
  doing: "changing your views",
  operation: "tableViews.manage",
  missingMessage: "This view is no longer saved. It may have been deleted in another browser. Refresh to see your views.",
};

/** The order of `views` with the view at `index` moved one place by `step`. */
export function movedViewIds(views: readonly NamedTableViewJSON[], index: number, step: -1 | 1): string[] {
  const ids = views.map((view) => view.id);
  const target = index + step;
  if (target < 0 || target >= ids.length) return ids;
  [ids[index], ids[target]] = [ids[target], ids[index]];
  return ids;
}

/**
 * Rename, reorder, delete and star a table's saved views (#6717). Opened from the Views menu, and
 * from Settings › Tables. The Default view is not listed: it is the page's own, and cannot change.
 */
export function ManageViewsDialog(
  props: Readonly<{
    open: boolean;
    tableKey: string;
    /** The table's name, as the reader knows it: "Proposals". */
    title: string;
    views: readonly NamedTableViewJSON[];
    onClose: () => void;
  }>,
) {
  const { open, tableKey, title, views, onClose } = props;
  const session = useSession();
  const titleId = useId();
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [renaming, setRenaming] = useState<{ id: string; name: string; touched: boolean } | undefined>();
  const [deletingId, setDeletingId] = useState<string | undefined>();
  const [updateView, updateState] = useUpdateTableViewMutation();
  const [deleteView, deleteState] = useDeleteTableViewMutation();
  const [reorderViews, reorderState] = useReorderTableViewsMutation();
  const [starView, starState] = useStarTableViewMutation();
  const busy = updateState.isLoading || deleteState.isLoading || reorderState.isLoading || starState.isLoading;

  async function change(request: () => { unwrap: () => Promise<unknown> }): Promise<boolean> {
    try {
      await request().unwrap();
      return true;
    } catch (error) {
      showErrorSnackbar(error, MANAGE_VIEWS_FAILURE);
      return false;
    }
  }

  async function rename(event: FormEvent, view: NamedTableViewJSON) {
    event.preventDefault();
    if (!renaming || !session) return;
    const problem = viewNameProblem(renaming.name, views, view.id);
    if (problem !== undefined) {
      setRenaming({ ...renaming, touched: true });
      return;
    }
    const name = renaming.name.trim();
    const state = fromTableViewStateJSON(view.state);
    if (!(await change(() => updateView({ session, tableKey, viewId: view.id, name, state })))) return;
    setRenaming(undefined);
  }

  async function remove(view: NamedTableViewJSON) {
    if (!session) return;
    if (!(await change(() => deleteView({ session, tableKey, viewId: view.id })))) return;
    setDeletingId(undefined);
    enqueueSnackbar(`Deleted "${view.name}".`, { variant: "success" });
  }

  function move(index: number, step: -1 | 1) {
    if (!session) return;
    void change(() => reorderViews({ session, tableKey, viewIds: movedViewIds(views, index, step) }));
  }

  function toggleStar(view: NamedTableViewJSON) {
    if (!session) return;
    void change(() => starView({ session, tableKey, viewId: view.isStarred ? null : view.id }));
  }

  function close() {
    setRenaming(undefined);
    setDeletingId(undefined);
    onClose();
  }

  return (
    <Dialog aria-labelledby={titleId} fullScreen={isPhone} fullWidth maxWidth="sm" open={open} onClose={close}>
      <DialogTitle id={titleId}>{`${title} views`}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2">
          {`The starred view opens when you visit ${title} from the side menu. Default view is the page's own, and always stays.`}
        </Typography>
        {views.length === 0 ? (
          <Typography sx={{ mt: 2 }}>You have no saved views. Save one with Save as new view in the Views menu.</Typography>
        ) : (
          <List aria-label={`Your ${title} views`}>
            {views.map((view, index) => {
              if (renaming?.id === view.id) {
                const problem = viewNameProblem(renaming.name, views, view.id);
                return (
                  <ListItem component="form" disableGutters divider key={view.id} onSubmit={(event: FormEvent) => void rename(event, view)}>
                    <Stack direction={{ sm: "row", xs: "column" }} spacing={1} sx={{ alignItems: { sm: "flex-start" }, width: "100%" }}>
                      <TextField
                        error={renaming.touched && problem !== undefined}
                        fullWidth
                        helperText={renaming.touched ? problem : undefined}
                        label={`New name for ${view.name}`}
                        onChange={(event) => setRenaming({ ...renaming, name: event.target.value })}
                        size="small"
                        value={renaming.name}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button disabled={busy} type="submit" variant="contained">Save</Button>
                        <Button onClick={() => setRenaming(undefined)}>Cancel</Button>
                      </Stack>
                    </Stack>
                  </ListItem>
                );
              }
              if (deletingId === view.id) {
                return (
                  <ListItem disableGutters divider key={view.id} sx={{ flexWrap: "wrap", gap: 1 }}>
                    <ListItemText primary={`Delete "${view.name}"?`} secondary="This cannot be undone." />
                    <Stack direction="row" spacing={1}>
                      <Button color="error" disabled={busy} onClick={() => void remove(view)} variant="contained">
                        Delete
                      </Button>
                      <Button onClick={() => setDeletingId(undefined)}>Cancel</Button>
                    </Stack>
                  </ListItem>
                );
              }
              return (
                <ListItem disableGutters divider key={view.id} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  <IconButton
                    aria-label={view.isStarred ? `Unstar ${view.name}` : `Star ${view.name}`}
                    aria-pressed={view.isStarred}
                    disabled={busy}
                    onClick={() => toggleStar(view)}
                    sx={{ color: view.isStarred ? "warning.main" : undefined }}
                  >
                    {view.isStarred ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                  <ListItemText
                    primary={view.name}
                    secondary={view.isStarred ? `Opens when you visit ${title}` : undefined}
                    sx={{ flex: "1 1 160px", minWidth: 0, overflowWrap: "anywhere" }}
                  />
                  <Stack direction="row">
                    <IconButton
                      aria-label={`Move ${view.name} up`}
                      disabled={busy || index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      aria-label={`Move ${view.name} down`}
                      disabled={busy || index === views.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                    <IconButton
                      aria-label={`Rename ${view.name}`}
                      onClick={() => setRenaming({ id: view.id, name: view.name, touched: false })}
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                    <IconButton aria-label={`Delete ${view.name}`} onClick={() => setDeletingId(view.id)}>
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
